import React from "react";
import { BookmarkPlus, Loader2, Check } from "lucide-react";

/**
 * V1.7.7 — "Save plan to my library" quick-action chip.
 *
 * Appears in the chat panel just above SuggestedActionChips after a
 * successful Simple-Mode chat round-trip completes. Lets the user fork
 * the current blueprint into a new "snapshot" design they can iterate
 * on independently from the live project.
 *
 * Three states:
 *   idle    — cyan outlined chip, click to trigger save
 *   saving  — spinner, button disabled
 *   saved   — emerald confirmation (auto-dismisses ~2s later)
 *
 * Hidden when `visible` is false so it never adds visual noise outside
 * the moment right after a chat completes.
 */
export default function SaveSnapshotChip({
  visible = false,
  saving = false,
  saved = false,
  onSave,
  onDismiss,
}) {
  if (!visible) return null;

  let tone = "border-brass-700/60 bg-brass-950/30 text-brass-200 hover:bg-brass-900/50 hover:border-brass-500 hover:text-brass-50";
  let icon = <BookmarkPlus className="w-3 h-3" />;
  let label = "// Save plan to my library";
  if (saving) {
    tone = "border-zinc-700 bg-zinc-900 text-zinc-300 cursor-wait";
    icon = <Loader2 className="w-3 h-3 animate-spin" />;
    label = "// Saving snapshot...";
  } else if (saved) {
    tone = "border-emerald-700/60 bg-emerald-950/30 text-emerald-200";
    icon = <Check className="w-3 h-3" />;
    label = "// Saved to library";
  }

  return (
    <div
      data-testid="save-snapshot-chip-row"
      className="px-3 py-2 border-t border-zinc-800/80 bg-zinc-950/60 flex items-center justify-between gap-2"
    >
      <button
        type="button"
        data-testid="save-snapshot-chip"
        onClick={onSave}
        disabled={saving || saved}
        className={
          "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors " +
          tone
        }
      >
        {icon}
        {label}
      </button>
      {!saving && !saved && onDismiss && (
        <button
          type="button"
          data-testid="save-snapshot-dismiss"
          onClick={onDismiss}
          className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="Dismiss save snapshot chip"
        >
          dismiss
        </button>
      )}
    </div>
  );
}
